import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ActionIcon,
  Alert,
  Badge,
  Group,
  Loader,
  NumberInput,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconExternalLink, IconGripVertical, IconPin, IconTrash } from '@tabler/icons-react';

import { BasicButton } from '@components/Button';
import { PageTitle } from '@components/shared';
import entityColor from '@util/entityColor';
import entityIcon from '@util/entityIcon';
import { calendarDateValue } from '@util/formatters';
import { toErrorMessage } from '@util/toErrorMessage';

const DAY_KIND_OPTIONS = [
  { value: '1', label: 'Mandatory lift' },
  { value: '2', label: 'Mandatory rest' },
  { value: '3', label: 'Optional recovery' },
];

const DAY_KIND_BY_LABEL = {
  mandatory_lift: '1',
  mandatory_rest: '2',
  optional_recovery: '3',
};

function formatLabel(value) {
  if (!value) return '—';
  return String(value).replace(/_/g, ' ');
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatSet(set) {
  const bits = [];
  const source = Number(set.prescription_source || 1);
  const isPercent = source === 3 && set.percent_of_max != null;
  if (isPercent) {
    bits.push(`${Number(set.percent_of_max)}%`);
  } else if (set.prescribed_weight != null) {
    bits.push(`${Number(set.prescribed_weight)} lb`);
  }
  if (set.prescribed_reps != null) bits.push(`${set.prescribed_reps} reps`);
  if (set.prescribed_duration_seconds != null) {
    bits.push(`${set.prescribed_duration_seconds}s`);
  }
  if (set.prescribed_distance != null) {
    const unit = set.prescribed_distance_unit || 'yd';
    bits.push(`${Number(set.prescribed_distance)} ${unit}`);
  }
  if (set.prescribed_how) bits.push(set.prescribed_how);
  if (set.prescribed_rest_seconds) bits.push(`${set.prescribed_rest_seconds}s rest`);
  if (set.estimated_rpe != null) bits.push(`RPE ${set.estimated_rpe}`);
  return bits.join(' · ') || '—';
}

function slotStatusColor(status) {
  if (status === 'completed') return 'teal';
  if (status === 'missed') return 'red';
  if (status === 'skipped') return 'gray';
  return 'blue';
}

function isPendingSlot(slot) {
  return (slot?.status_formatted || '') === 'pending';
}

function isLiftSlot(slot) {
  const kind = Number(slot?.day_kind);
  const label = slot?.day_kind_formatted;
  return (
    kind === 1 ||
    kind === 3 ||
    label === 'mandatory_lift' ||
    label === 'optional_recovery'
  );
}

function slotDateObject(slot) {
  const raw = calendarDateValue(slot?.recommended_on);
  return raw ? new Date(`${raw}T00:00:00`) : null;
}

function slotWorkoutId(slot) {
  return slot?.workout_id || slot?.workout?.id || null;
}

function WorkoutExerciseList({ workout, loading, error }) {
  if (loading) {
    return (
      <Group gap="sm">
        <Loader size="sm" type="dots" />
        <Text size="sm" c="dimmed">
          Loading workout…
        </Text>
      </Group>
    );
  }

  if (error) {
    return (
      <Alert variant="light" color="yellow" title="Couldn’t load workout">
        {toErrorMessage(error)}
      </Alert>
    );
  }

  if (!workout) {
    return (
      <Text size="sm" c="dimmed">
        Workout not found
      </Text>
    );
  }

  const exercises = (workout.exercises || [])
    .slice()
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

  if (exercises.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No exercises on this workout.
      </Text>
    );
  }

  return (
    <Stack gap={6}>
      {exercises.map((exerciseRow, index) => (
        <Stack key={exerciseRow.id || index} gap={2}>
          <Text size="sm" fw={600}>
            {index + 1}. {exerciseRow.exercise?.name || 'Exercise'}
          </Text>
          {(exerciseRow.sets || []).length > 0 && (
            <Text size="xs" c="dimmed">
              {(exerciseRow.sets || [])
                .slice()
                .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
                .map((set) => formatSet(set))
                .join(' · ')}
            </Text>
          )}
        </Stack>
      ))}
    </Stack>
  );
}

function SortableWorkoutCard({ join, onRemove, disabled }) {
  const [expanded, setExpanded] = useState(false);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: join.id });

  const detailQuery = useQuery({
    queryKey: ['admin-workout-preview', join.workoutId],
    queryFn: async ({ signal }) => {
      const { data } = await axios.get(`/workouts/${join.workoutId}/`, { signal });
      return data.response?.[0];
    },
    enabled: expanded && Boolean(join.workoutId),
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.85 : 1,
    cursor: 'pointer',
  };

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      p="sm"
      withBorder
      radius="sm"
      onClick={() => setExpanded((prev) => !prev)}
    >
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          <ActionIcon
            variant="subtle"
            color="gray"
            {...attributes}
            {...listeners}
            aria-label="Drag to reorder"
            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
            disabled={disabled}
            onClick={(event) => event.stopPropagation()}
          >
            <IconGripVertical size={18} />
          </ActionIcon>
          <Stack gap={2} style={{ minWidth: 0 }}>
            <Group gap="xs">
              <Badge size="sm" variant="light">
                Pos {join.position + 1}
              </Badge>
              {join.locked && (
                <Badge size="sm" variant="outline" color="gray">
                  locked
                </Badge>
              )}
            </Group>
            <Text fw={600} truncate>
              {join.name}
            </Text>
          </Stack>
        </Group>
        <Group gap={4} wrap="nowrap">
          <ActionIcon
            component={Link}
            to={`/workouts/${join.workoutId}`}
            variant="subtle"
            color="gray"
            aria-label="Open workout detail"
            onClick={(event) => event.stopPropagation()}
          >
            <IconExternalLink size={16} />
          </ActionIcon>
          <ActionIcon
            variant="subtle"
            color="red"
            onClick={(event) => {
              event.stopPropagation();
              onRemove(join.workoutId);
            }}
            aria-label="Remove workout"
            disabled={disabled}
          >
            <IconTrash size={16} />
          </ActionIcon>
        </Group>
      </Group>

      {expanded && (
        <Stack gap="sm" mt="sm" onClick={(event) => event.stopPropagation()}>
          <Text size="xs" c="dimmed">
            {(detailQuery.data?.exercises || []).length} exercises
            {detailQuery.data?.planned_set_count != null
              ? ` · ${detailQuery.data.planned_set_count} sets`
              : ''}
          </Text>
          <WorkoutExerciseList
            workout={detailQuery.data}
            loading={detailQuery.isLoading}
            error={detailQuery.isError ? detailQuery.error : null}
          />
        </Stack>
      )}
    </Paper>
  );
}

export default function AdminTrainingBlockDetail() {
  const { athleteId, blockId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [blockName, setBlockName] = useState('');
  const [blockStart, setBlockStart] = useState(null);
  const [blockEnd, setBlockEnd] = useState(null);
  const [blockPattern, setBlockPattern] = useState(['1', '2', '1', '2']);
  const [blockSaving, setBlockSaving] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmInactivate, setConfirmInactivate] = useState(false);

  const [orderedWorkoutIds, setOrderedWorkoutIds] = useState([]);
  const [workoutsSaving, setWorkoutsSaving] = useState(false);
  const [pickWorkoutId, setPickWorkoutId] = useState(null);
  const [layoutBusy, setLayoutBusy] = useState(false);

  const [slotBusyId, setSlotBusyId] = useState(null);
  const [confirmSlotDeleteId, setConfirmSlotDeleteId] = useState(null);

  const [creatingWorkout, setCreatingWorkout] = useState(false);
  const [newWorkoutName, setNewWorkoutName] = useState('');
  const [newExerciseId, setNewExerciseId] = useState(null);
  const [newRxMode, setNewRxMode] = useState('weight');
  const [newWeight, setNewWeight] = useState(135);
  const [newPercent, setNewPercent] = useState(80);
  const [newReps, setNewReps] = useState(5);
  const [newDuration, setNewDuration] = useState(30);
  const [newDistance, setNewDistance] = useState(40);
  const [newRest, setNewRest] = useState(90);
  const [workoutSaving, setWorkoutSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const athleteQuery = useQuery({
    queryKey: ['admin-athlete', athleteId],
    queryFn: async ({ signal }) => {
      const { data } = await axios.get(`/athletes/${athleteId}/`, { signal });
      return data.response?.[0];
    },
    enabled: Boolean(athleteId),
  });

  const blockQuery = useQuery({
    queryKey: ['admin-training-block', blockId],
    queryFn: async ({ signal }) => {
      const { data } = await axios.get(`/training-blocks/${blockId}/`, { signal });
      return data.response?.[0];
    },
    enabled: Boolean(blockId),
  });

  const workoutsQuery = useQuery({
    queryKey: ['admin-workouts'],
    queryFn: async ({ signal }) => {
      const { data } = await axios.post(
        '/retrieve-workouts/',
        { page: 0, page_size: 100 },
        { signal },
      );
      return data.response?.[0]?.data || [];
    },
  });

  const exercisesQuery = useQuery({
    queryKey: ['admin-exercises-picker'],
    queryFn: async ({ signal }) => {
      const { data } = await axios.post(
        '/retrieve-exercises/',
        { page: 0, page_size: 500, status: 2 },
        { signal },
      );
      return data.response?.[0]?.data || [];
    },
    enabled: creatingWorkout,
  });

  const pickedWorkoutQuery = useQuery({
    queryKey: ['admin-workout-preview', pickWorkoutId],
    queryFn: async ({ signal }) => {
      const { data } = await axios.get(`/workouts/${pickWorkoutId}/`, { signal });
      return data.response?.[0];
    },
    enabled: Boolean(pickWorkoutId),
  });

  const athlete = athleteQuery.data;
  const block = blockQuery.data;
  const blockWorkouts = block?.workouts || [];
  const patternDays = block?.pattern_days || [];
  const slots = useMemo(() => {
    const all = block?.slots || [];
    const start = calendarDateValue(block?.start_date);
    if (!start) return all;
    return all.filter((slot) => {
      const on = calendarDateValue(slot.recommended_on);
      if (!on) return false;
      return on >= start;
    });
  }, [block?.slots, block?.start_date]);
  const libraryWorkouts = workoutsQuery.data || [];
  const libraryExercises = exercisesQuery.data || [];
  const isDeleted = block?.status_formatted === 'deleted';
  const isActive = block?.status_formatted === 'active';
  const isInactive = block?.status_formatted === 'inactive';
  const editsLocked = isDeleted || statusBusy;

  useEffect(() => {
    if (!block) return;
    setBlockName(block.name || '');
    const start = calendarDateValue(block.start_date);
    const end = calendarDateValue(block.end_date);
    setBlockStart(start ? new Date(`${start}T00:00:00`) : null);
    setBlockEnd(end ? new Date(`${end}T00:00:00`) : null);
    const pattern = (block.pattern_days || [])
      .slice()
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
      .map((day) => {
        if (day.day_kind != null) return String(day.day_kind);
        return DAY_KIND_BY_LABEL[day.day_kind_formatted] || '1';
      });
    setBlockPattern(pattern.length ? pattern : ['1', '2', '1', '2']);
    setConfirmDelete(false);
    setConfirmInactivate(false);
  }, [block?.id, block?.name, block?.start_date, block?.end_date, block?.pattern_days, block?.status_formatted]);

  useEffect(() => {
    const ids = (block?.workouts || [])
      .slice()
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
      .map((row) => row.workout_id || row.workout?.id)
      .filter(Boolean);
    setOrderedWorkoutIds(ids);
  }, [block?.id, block?.workouts]);

  const workoutOptions = useMemo(
    () =>
      libraryWorkouts.map((workout) => ({
        value: workout.id,
        label: workout.locked
          ? `${workout.name} (locked)`
          : workout.name || workout.id,
      })),
    [libraryWorkouts],
  );

  const exerciseOptions = useMemo(
    () =>
      libraryExercises.map((exercise) => ({
        value: String(exercise.id),
        label: exercise.name || exercise.id,
      })),
    [libraryExercises],
  );

  const selectedCreateExercise = useMemo(
    () => libraryExercises.find((exercise) => String(exercise.id) === String(newExerciseId)),
    [libraryExercises, newExerciseId],
  );
  const createFlags = {
    usesWeight: selectedCreateExercise?.uses_weight ?? true,
    usesReps: selectedCreateExercise?.uses_reps ?? true,
    usesDuration: selectedCreateExercise?.uses_duration ?? false,
    usesDistance: selectedCreateExercise?.uses_distance ?? false,
  };

  const orderedJoins = useMemo(() => {
    return orderedWorkoutIds.map((workoutId, index) => {
      const fromBlock = blockWorkouts.find(
        (row) => (row.workout_id || row.workout?.id) === workoutId,
      );
      const fromLibrary = libraryWorkouts.find((row) => row.id === workoutId);
      return {
        id: fromBlock?.id || `draft-${workoutId}`,
        workoutId,
        position: index,
        name: fromBlock?.workout?.name || fromLibrary?.name || workoutId,
        locked: Boolean(fromBlock?.workout?.locked || fromLibrary?.locked),
      };
    });
  }, [orderedWorkoutIds, blockWorkouts, libraryWorkouts]);

  function reloadBlock() {
    queryClient.invalidateQueries({ queryKey: ['admin-training-block', blockId] });
    queryClient.invalidateQueries({ queryKey: ['admin-training-blocks', athleteId] });
    queryClient.invalidateQueries({ queryKey: ['admin-athlete', athleteId] });
    queryClient.invalidateQueries({ queryKey: ['admin-athlete-calendar-events', athleteId] });
  }

  function onActivate() {
    if (!blockId || isDeleted) return;
    setStatusBusy(true);
    axios
      .post(`/training-blocks/${blockId}/activate/`, {})
      .then(() => {
        toast.success('Training block activated');
        reloadBlock();
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setStatusBusy(false));
  }

  function onInactivate() {
    if (!blockId || isDeleted) return;
    setStatusBusy(true);
    axios
      .post(`/training-blocks/${blockId}/inactivate/`, {})
      .then(() => {
        toast.success('Training block inactivated');
        reloadBlock();
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setStatusBusy(false));
  }

  function onDeleteBlock() {
    if (!blockId || isDeleted) return;
    setStatusBusy(true);
    axios
      .delete(`/training-blocks/${blockId}/`)
      .then(() => {
        toast.success('Training block deleted');
        queryClient.invalidateQueries({ queryKey: ['admin-training-blocks', athleteId] });
        queryClient.invalidateQueries({ queryKey: ['admin-athlete', athleteId] });
        navigate(`/athletes/${athleteId}`);
      })
      .catch((error) => {
        toast.error(toErrorMessage(error));
        setStatusBusy(false);
        setConfirmDelete(false);
      });
  }

  function persistWorkouts(nextIds) {
    if (!blockId || isDeleted) return Promise.resolve();
    setWorkoutsSaving(true);
    return axios
      .put(`/training-blocks/${blockId}/workouts/`, {
        workout_ids: nextIds,
      })
      .then(() => {
        toast.success('Block workouts saved');
        reloadBlock();
        queryClient.invalidateQueries({ queryKey: ['admin-workouts'] });
      })
      .catch((error) => {
        toast.error(toErrorMessage(error));
        reloadBlock();
      })
      .finally(() => setWorkoutsSaving(false));
  }

  function addPickedWorkout() {
    if (!pickWorkoutId) return;
    if (orderedWorkoutIds.includes(pickWorkoutId)) {
      toast.error('Workout already on this block');
      setPickWorkoutId(null);
      return;
    }
    const nextIds = [...orderedWorkoutIds, pickWorkoutId];
    setOrderedWorkoutIds(nextIds);
    setPickWorkoutId(null);
    persistWorkouts(nextIds);
  }

  function removeOrderedWorkout(workoutId) {
    const nextIds = orderedWorkoutIds.filter((item) => item !== workoutId);
    setOrderedWorkoutIds(nextIds);
    persistWorkouts(nextIds);
  }

  function onDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = orderedJoins.findIndex((join) => join.id === active.id);
    const newIndex = orderedJoins.findIndex((join) => join.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const nextIds = arrayMove(orderedWorkoutIds, oldIndex, newIndex);
    setOrderedWorkoutIds(nextIds);
    persistWorkouts(nextIds);
  }

  function onSaveBlock(event) {
    event.preventDefault();
    if (!blockId || isDeleted) return;
    const start = calendarDateValue(blockStart);
    const end = calendarDateValue(blockEnd);
    if (!blockName.trim() || !start || !end || blockPattern.length === 0) {
      toast.error('Name, dates, and pattern are required');
      return;
    }
    setBlockSaving(true);
    axios
      .put(`/training-blocks/${blockId}/`, {
        name: blockName.trim(),
        start_date: start,
        end_date: end,
        pattern: blockPattern.map((value) => Number(value)),
      })
      .then(() => {
        toast.success('Training block saved');
        if ((slots || []).length > 0) {
          toast('Pending slots may be stale — run Layout slots to rebuild.');
        }
        reloadBlock();
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setBlockSaving(false));
  }

  function addPatternDay() {
    setBlockPattern((prev) => [...prev, '1']);
  }

  function updatePatternDay(index, value) {
    setBlockPattern((prev) => prev.map((item, i) => (i === index ? value : item)));
  }

  function removePatternDay(index) {
    setBlockPattern((prev) => prev.filter((_, i) => i !== index));
  }

  function onUpdateSlot(slotId, payload) {
    if (!slotId || editsLocked) return;
    setSlotBusyId(slotId);
    axios
      .put(`/workout-slots/${slotId}/`, payload)
      .then(() => {
        toast.success('Slot updated');
        setConfirmSlotDeleteId(null);
        reloadBlock();
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setSlotBusyId(null));
  }

  function onDeleteSlot(slotId) {
    if (!slotId || editsLocked) return;
    setSlotBusyId(slotId);
    axios
      .delete(`/workout-slots/${slotId}/`)
      .then(() => {
        toast.success('Slot deleted');
        setConfirmSlotDeleteId(null);
        reloadBlock();
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setSlotBusyId(null));
  }

  function onLayoutSlots() {
    if (!blockId || isDeleted) return;
    setLayoutBusy(true);
    axios
      .post(`/training-blocks/${blockId}/layout/`, {})
      .then(() => {
        toast.success('Slots laid out');
        reloadBlock();
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setLayoutBusy(false));
  }

  function onCreateWorkout(event) {
    event.preventDefault();
    if (!blockId || isDeleted) return;
    if (!newWorkoutName.trim() || !newExerciseId) {
      toast.error('Workout name and an exercise are required');
      return;
    }
    const isPercent = createFlags.usesWeight && newRxMode === 'percent';
    setWorkoutSaving(true);
    axios
      .post('/workouts/', {
        name: newWorkoutName.trim(),
        training_block_id: blockId,
        athlete_id: athleteId,
        exercises: [
          {
            exercise_id: newExerciseId,
            sets: [
              {
                prescribed_weight: createFlags.usesWeight && !isPercent ? newWeight : null,
                percent_of_max: isPercent ? newPercent : null,
                prescribed_reps: createFlags.usesReps ? newReps : null,
                prescribed_duration_seconds: createFlags.usesDuration ? newDuration : null,
                prescribed_distance: createFlags.usesDistance ? newDistance : null,
                prescribed_distance_unit: createFlags.usesDistance ? 'yd' : null,
                prescribed_rest_seconds: newRest,
                prescription_source: isPercent ? 3 : 1,
              },
            ],
          },
        ],
      })
      .then(() => {
        toast.success('Workout created and attached');
        setCreatingWorkout(false);
        setNewWorkoutName('');
        setNewExerciseId(null);
        reloadBlock();
        queryClient.invalidateQueries({ queryKey: ['admin-workouts'] });
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setWorkoutSaving(false));
  }

  if (blockQuery.isLoading || athleteQuery.isLoading) {
    return (
      <Group gap="sm">
        <Loader size="sm" type="dots" />
        <Text c="dimmed">Loading training block…</Text>
      </Group>
    );
  }

  if (blockQuery.isError || !block) {
    return (
      <Alert variant="light" color="yellow" title="Oops!">
        {blockQuery.isError
          ? toErrorMessage(blockQuery.error)
          : 'Training block not found'}
      </Alert>
    );
  }

  const athleteName = athlete?.full_name || 'Athlete';

  return (
    <div>
      <Text size="sm" mb="sm">
        <Link to="/" style={{ color: 'inherit' }}>
          Athletes
        </Link>
        {' / '}
        <Link to={`/athletes/${athleteId}`} style={{ color: 'inherit' }}>
          {athleteName}
        </Link>
        {' / '}
        <Text span c="dimmed">
          {block.name}
        </Text>
      </Text>

      <PageTitle
        title={block.name}
        subtitle={`${formatDate(block.start_date)} – ${formatDate(block.end_date)}`}
        icon={entityIcon.workout()}
        iconColor={entityColor.athlete}
      />

      <Stack gap="lg" mt="md">
        {isDeleted ? (
          <Alert variant="light" color="red" title="Deleted">
            This training block is soft-deleted and cannot be edited.
          </Alert>
        ) : null}

        <Paper
          component="form"
          onSubmit={onSaveBlock}
          p="md"
          withBorder
          radius="sm"
        >
          <Stack gap="sm">
            <Group justify="space-between" align="center">
              <Text fw={700}>Block details</Text>
              <Badge
                size="sm"
                variant="light"
                color={
                  isActive ? 'teal' : isInactive ? 'gray' : isDeleted ? 'red' : 'blue'
                }
              >
                {formatLabel(block.status_formatted)}
              </Badge>
            </Group>
            <TextInput
              label="Name"
              value={blockName}
              onChange={(event) => setBlockName(event.currentTarget.value)}
              required
              disabled={editsLocked || blockSaving}
            />
            <Group grow>
              <DatePickerInput
                label="Start"
                value={blockStart}
                onChange={setBlockStart}
                required
                disabled={editsLocked || blockSaving}
              />
              <DatePickerInput
                label="End"
                value={blockEnd}
                onChange={setBlockEnd}
                required
                disabled={editsLocked || blockSaving}
              />
            </Group>
            <Stack gap="xs">
              <Text size="sm" fw={600}>
                Pattern
              </Text>
              {blockPattern.map((dayKind, index) => (
                <Group key={`pattern-${index}`} gap="xs" wrap="nowrap">
                  <Select
                    data={DAY_KIND_OPTIONS}
                    value={dayKind}
                    onChange={(value) => updatePatternDay(index, value || '1')}
                    style={{ flex: 1 }}
                    disabled={editsLocked || blockSaving}
                  />
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    onClick={() => removePatternDay(index)}
                    disabled={editsLocked || blockSaving || blockPattern.length <= 1}
                    aria-label="Remove pattern day"
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              ))}
              <BasicButton
                text="Add pattern day"
                onClick={addPatternDay}
                disabled={editsLocked || blockSaving}
              />
            </Stack>
            {!isDeleted ? (
              <Group>
                <BasicButton
                  text={blockSaving ? 'Saving…' : 'Save block'}
                  type="submit"
                  disabled={blockSaving || statusBusy}
                />
              </Group>
            ) : null}
            <Group gap="xs" wrap="wrap">
              {!isDeleted && !isActive && !confirmInactivate && !confirmDelete ? (
                <BasicButton
                  text="Activate"
                  onClick={onActivate}
                  loading={statusBusy}
                  disabled={statusBusy || blockSaving}
                  color="teal"
                  variant="light"
                />
              ) : null}
              {!isDeleted && !isInactive && !confirmInactivate && !confirmDelete ? (
                <BasicButton
                  text="Inactivate"
                  onClick={() => {
                    setConfirmDelete(false);
                    setConfirmInactivate(true);
                  }}
                  disabled={statusBusy || blockSaving}
                  color="gray"
                  variant="light"
                />
              ) : null}
              {confirmInactivate ? (
                <Stack gap={4} maw={520}>
                  <Text size="sm">
                    Pause this block? Pending sessions stay on the plan but will not appear
                    on the kiosk or calendar until you activate again. Any open kiosk
                    session will be abandoned.
                  </Text>
                  <Group gap="xs">
                    <BasicButton
                      text="Confirm inactivate"
                      onClick={onInactivate}
                      loading={statusBusy}
                      disabled={statusBusy}
                      color="gray"
                      variant="filled"
                    />
                    <BasicButton
                      text="Cancel"
                      onClick={() => setConfirmInactivate(false)}
                      disabled={statusBusy}
                      variant="light"
                    />
                  </Group>
                </Stack>
              ) : null}
              {!isDeleted && !confirmInactivate && !confirmDelete ? (
                <BasicButton
                  text="Delete block"
                  onClick={() => {
                    setConfirmInactivate(false);
                    setConfirmDelete(true);
                  }}
                  disabled={statusBusy || blockSaving}
                  color="red"
                  variant="light"
                />
              ) : null}
              {confirmDelete ? (
                <Stack gap={4} maw={520}>
                  <Text size="sm">
                    Delete this training block? Remaining pending sessions will be removed.
                    Completed history stays. Any open kiosk session will be abandoned.
                  </Text>
                  <Group gap="xs">
                    <BasicButton
                      text="Confirm delete"
                      onClick={onDeleteBlock}
                      loading={statusBusy}
                      disabled={statusBusy}
                      color="red"
                      variant="filled"
                    />
                    <BasicButton
                      text="Cancel"
                      onClick={() => setConfirmDelete(false)}
                      disabled={statusBusy}
                      variant="light"
                    />
                  </Group>
                </Stack>
              ) : null}
            </Group>
            {patternDays.length > 0 && (
              <Text size="xs" c="dimmed">
                Saved pattern:{' '}
                {patternDays
                  .slice()
                  .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
                  .map((day) => formatLabel(day.day_kind_formatted))
                  .join(' → ')}
              </Text>
            )}
          </Stack>
        </Paper>

        <Paper p="md" withBorder radius="sm">
          <Stack gap="sm">
            <Group justify="space-between" align="center">
              <Text fw={700}>Workouts</Text>
              <BasicButton
                text={layoutBusy ? 'Laying out…' : 'Layout slots'}
                onClick={onLayoutSlots}
                disabled={
                  editsLocked ||
                  layoutBusy ||
                  orderedWorkoutIds.length === 0 ||
                  workoutsSaving
                }
              />
            </Group>
            <Text size="xs" c="dimmed">
              Drag cards to set training-block position. Changes save immediately.
            </Text>

            {orderedJoins.length === 0 ? (
              <Text size="sm" c="dimmed">
                No workouts attached yet.
              </Text>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={isDeleted ? undefined : onDragEnd}
              >
                <SortableContext
                  items={orderedJoins.map((join) => join.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <Stack gap={8}>
                    {orderedJoins.map((join) => (
                      <SortableWorkoutCard
                        key={join.id}
                        join={join}
                        onRemove={removeOrderedWorkout}
                        disabled={workoutsSaving || editsLocked}
                      />
                    ))}
                  </Stack>
                </SortableContext>
              </DndContext>
            )}

            <Select
              label="Add from library"
              placeholder="Pick a workout"
              data={workoutOptions}
              value={pickWorkoutId}
              onChange={setPickWorkoutId}
              searchable
              clearable
              disabled={workoutsSaving || editsLocked}
            />

            {pickWorkoutId && (
              <Paper p="sm" withBorder radius="sm" bg="var(--mantine-color-gray-0)">
                <Stack gap="sm">
                  {pickedWorkoutQuery.isLoading ? (
                    <WorkoutExerciseList loading />
                  ) : pickedWorkoutQuery.isError || !pickedWorkoutQuery.data ? (
                    <WorkoutExerciseList
                      error={pickedWorkoutQuery.isError ? pickedWorkoutQuery.error : null}
                    />
                  ) : (
                    <>
                      <Group justify="space-between" align="flex-start" wrap="nowrap">
                        <Stack gap={2} style={{ minWidth: 0 }}>
                          <Group gap="xs">
                            <Text fw={700} truncate>
                              {pickedWorkoutQuery.data.name}
                            </Text>
                            {pickedWorkoutQuery.data.locked && (
                              <Badge size="sm" variant="outline" color="gray">
                                locked
                              </Badge>
                            )}
                            <ActionIcon
                              component={Link}
                              to={`/workouts/${pickWorkoutId}`}
                              variant="subtle"
                              color="gray"
                              aria-label="Open workout detail"
                            >
                              <IconExternalLink size={16} />
                            </ActionIcon>
                          </Group>
                          <Text size="xs" c="dimmed">
                            {(pickedWorkoutQuery.data.exercises || []).length} exercises
                            {pickedWorkoutQuery.data.planned_set_count != null
                              ? ` · ${pickedWorkoutQuery.data.planned_set_count} sets`
                              : ''}
                          </Text>
                        </Stack>
                        <BasicButton
                          text={workoutsSaving ? 'Adding…' : 'Add'}
                          onClick={addPickedWorkout}
                          disabled={workoutsSaving || editsLocked}
                        />
                      </Group>

                      <WorkoutExerciseList workout={pickedWorkoutQuery.data} />
                    </>
                  )}
                </Stack>
              </Paper>
            )}

            {!creatingWorkout ? (
              <BasicButton
                text="Create workout"
                onClick={() => setCreatingWorkout(true)}
                disabled={workoutsSaving || editsLocked}
              />
            ) : (
              <Paper
                component="form"
                onSubmit={onCreateWorkout}
                p="sm"
                withBorder
                radius="sm"
              >
                <Stack gap="sm">
                  <Text fw={700}>Create & attach workout</Text>
                  <TextInput
                    label="Name"
                    value={newWorkoutName}
                    onChange={(event) => setNewWorkoutName(event.currentTarget.value)}
                    required
                  />
                  <Select
                    label="Exercise"
                    data={exerciseOptions}
                    value={newExerciseId}
                    onChange={setNewExerciseId}
                    searchable
                    required
                  />
                  {selectedCreateExercise && createFlags.usesWeight ? (
                    <div>
                      <Text size="sm" fw={500} mb={4}>
                        Prescription
                      </Text>
                      <SegmentedControl
                        fullWidth
                        value={newRxMode}
                        onChange={setNewRxMode}
                        data={[
                          { label: 'Weight (lb)', value: 'weight' },
                          { label: '% of max', value: 'percent' },
                        ]}
                      />
                    </div>
                  ) : null}
                  <Group grow>
                    {selectedCreateExercise && createFlags.usesWeight ? (
                      newRxMode === 'percent' ? (
                        <NumberInput
                          label="% of max"
                          value={newPercent}
                          onChange={setNewPercent}
                          min={1}
                          max={100}
                        />
                      ) : (
                        <NumberInput
                          label="Weight (lb)"
                          value={newWeight}
                          onChange={setNewWeight}
                          min={0}
                        />
                      )
                    ) : null}
                    {(!selectedCreateExercise || createFlags.usesReps) && (
                      <NumberInput
                        label="Reps"
                        value={newReps}
                        onChange={setNewReps}
                        min={1}
                      />
                    )}
                    {selectedCreateExercise && createFlags.usesDuration ? (
                      <NumberInput
                        label="Duration (sec)"
                        value={newDuration}
                        onChange={setNewDuration}
                        min={1}
                      />
                    ) : null}
                    {selectedCreateExercise && createFlags.usesDistance ? (
                      <NumberInput
                        label="Distance (yd)"
                        value={newDistance}
                        onChange={setNewDistance}
                        min={0}
                        decimalScale={1}
                      />
                    ) : null}
                    <NumberInput
                      label="Rest (sec)"
                      value={newRest}
                      onChange={setNewRest}
                      min={0}
                    />
                  </Group>
                  <Group>
                    <BasicButton
                      text={workoutSaving ? 'Saving…' : 'Create & attach'}
                      type="submit"
                      disabled={workoutSaving}
                    />
                    <BasicButton
                      text="Cancel"
                      onClick={() => setCreatingWorkout(false)}
                      disabled={workoutSaving}
                    />
                  </Group>
                </Stack>
              </Paper>
            )}
          </Stack>
        </Paper>

        <Paper p="md" withBorder radius="sm">
          <Stack gap="sm">
            <Text fw={700}>Slots{slots.length ? ` (${slots.length})` : ''}</Text>
            <Text size="xs" c="dimmed">
              Pending rows can move dates or swap workouts. That pins the slot so Layout
              slots keeps it.
            </Text>
            {slots.length === 0 ? (
              <Text size="sm" c="dimmed">
                No slots yet. Attach workouts and run Layout slots.
              </Text>
            ) : (
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Date</Table.Th>
                    <Table.Th>Kind</Table.Th>
                    <Table.Th>Workout</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Complete</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {slots.map((slot) => {
                    const pending = isPendingSlot(slot);
                    const canEdit = pending && !editsLocked;
                    const busy = slotBusyId === slot.id || layoutBusy;
                    const currentWorkoutId = slotWorkoutId(slot);
                    const workoutOptionsForSlot = orderedJoins.map((join) => ({
                      value: join.workoutId,
                      label: join.name,
                    }));
                    if (
                      currentWorkoutId &&
                      !workoutOptionsForSlot.some((opt) => opt.value === currentWorkoutId)
                    ) {
                      workoutOptionsForSlot.unshift({
                        value: currentWorkoutId,
                        label: slot.workout?.name || currentWorkoutId,
                      });
                    }
                    const confirmingDelete = confirmSlotDeleteId === slot.id;

                    return (
                      <Table.Tr key={slot.id}>
                        <Table.Td>
                          <Group gap="xs" wrap="nowrap">
                            {canEdit ? (
                              <DatePickerInput
                                size="xs"
                                value={slotDateObject(slot)}
                                minDate={blockStart || undefined}
                                maxDate={blockEnd || undefined}
                                disabled={busy}
                                popoverProps={{ withinPortal: true }}
                                onChange={(value) => {
                                  const next = calendarDateValue(value);
                                  const current = calendarDateValue(slot.recommended_on);
                                  if (!next || next === current) return;
                                  onUpdateSlot(slot.id, { recommended_on: next });
                                }}
                                style={{ minWidth: 148 }}
                              />
                            ) : (
                              <Text size="sm">{formatDate(slot.recommended_on)}</Text>
                            )}
                            {slot.manually_placed ? (
                              <Badge
                                size="xs"
                                variant="light"
                                color="gray"
                                leftSection={<IconPin size={10} />}
                              >
                                Pinned
                              </Badge>
                            ) : null}
                          </Group>
                        </Table.Td>
                        <Table.Td>{formatLabel(slot.day_kind_formatted)}</Table.Td>
                        <Table.Td>
                          {canEdit && isLiftSlot(slot) ? (
                            <Select
                              size="xs"
                              data={workoutOptionsForSlot}
                              value={currentWorkoutId || null}
                              searchable
                              clearable={false}
                              disabled={busy || workoutOptionsForSlot.length === 0}
                              onChange={(value) => {
                                if (!value || value === currentWorkoutId) return;
                                onUpdateSlot(slot.id, { workout_id: value });
                              }}
                              style={{ minWidth: 180 }}
                            />
                          ) : (
                            slot.workout?.name || '—'
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            size="sm"
                            variant="light"
                            color={slotStatusColor(slot.status_formatted)}
                          >
                            {formatLabel(slot.status_formatted)}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          {slot.percent_complete != null
                            ? `${slot.percent_complete}%`
                            : '—'}
                        </Table.Td>
                        <Table.Td>
                          {canEdit ? (
                            confirmingDelete ? (
                              <Group gap="xs" wrap="nowrap" justify="flex-end">
                                <BasicButton
                                  text="Confirm"
                                  size="xs"
                                  color="red"
                                  variant="filled"
                                  onClick={() => onDeleteSlot(slot.id)}
                                  loading={busy}
                                  disabled={busy}
                                />
                                <BasicButton
                                  text="Cancel"
                                  size="xs"
                                  color="gray"
                                  variant="outline"
                                  onClick={() => setConfirmSlotDeleteId(null)}
                                  disabled={busy}
                                />
                              </Group>
                            ) : (
                              <Group justify="flex-end">
                                <ActionIcon
                                  color="red"
                                  variant="subtle"
                                  title="Delete slot"
                                  disabled={busy}
                                  onClick={() => setConfirmSlotDeleteId(slot.id)}
                                >
                                  <IconTrash size={16} />
                                </ActionIcon>
                              </Group>
                            )
                          ) : null}
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            )}
          </Stack>
        </Paper>
      </Stack>
    </div>
  );
}
